import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  CheckCircle2,
  AlertTriangle,
  Columns,
  DollarSign,
  TrendingUp,
  Gift,
  User,
  Mail,
  Phone,
  UserCheck,
  Info
} from 'lucide-react'
import {
  UnifiedColumnMappingService,
  EarningsData,
  UnifiedColumnMapping
} from '@/services/unifiedColumnMappingService'
import { ColumnMappingService, ColumnMappingConfig } from '@/services/columnMappingService'
import { PlatformDetectionService } from '@/services/platformDetectionService'
import { Upload, Platform } from '@/types'

interface FieldMappingStepProps {
  upload: Upload
  identifierMapping: Record<string, string> // identifier fields mapping
  fieldMapping: Record<string, string> // earnings fields mapping
  onIdentifierMappingChange: (mapping: Record<string, string>) => void
  onFieldMappingChange: (mapping: Record<string, string>) => void
}

// Unified field configuration with priority ordering
const UNIFIED_FIELD_CONFIG = {
  // Required fields first
  driverName: {
    label: 'Förarnamn',
    icon: User,
    required: true,
    type: 'identifier' as const
  },
  grossEarnings: {
    label: 'Bruttoinkomst',
    icon: TrendingUp,
    required: true,
    type: 'earnings' as const
  },
  netEarnings: {
    label: 'Nettoinkomst',
    icon: DollarSign,
    required: true,
    type: 'earnings' as const
  },
  // Optional fields
  tips: {
    label: 'Dricks',
    icon: Gift,
    required: false,
    type: 'earnings' as const
  },
  driverId: {
    label: 'Förar-ID',
    icon: UserCheck,
    required: false,
    type: 'identifier' as const
  },
  email: {
    label: 'E-post',
    icon: Mail,
    required: false,
    type: 'identifier' as const
  },
  phone: {
    label: 'Telefon',
    icon: Phone,
    required: false,
    type: 'identifier' as const
  },
  firstName: {
    label: 'Förnamn',
    icon: User,
    required: false,
    type: 'identifier' as const
  },
  lastName: {
    label: 'Efternamn',
    icon: User,
    required: false,
    type: 'identifier' as const
  },
  transactionId: {
    label: 'Transaktions-ID',
    icon: UserCheck,
    required: false,
    type: 'identifier' as const
  },
  description: {
    label: 'Beskrivning',
    icon: Info,
    required: false,
    type: 'earnings' as const
  },
} as const

export function FieldMappingStep({
  upload,
  identifierMapping,
  fieldMapping,
  onIdentifierMappingChange,
  onFieldMappingChange
}: FieldMappingStepProps) {
  const [unifiedMapping, setUnifiedMapping] = useState<UnifiedColumnMapping | null>(null)
  const [previewData, setPreviewData] = useState<EarningsData[]>([])
  const [previewIdentifierData, setPreviewIdentifierData] = useState<Record<string, any>[]>([])
  const [showDriverNameWarning, setShowDriverNameWarning] = useState<boolean>(false)

  // Memoize upload headers to prevent infinite re-renders
  const memoizedHeaders = useMemo(() => upload.headers || [], [upload.headers])

  // Get platform-specific configuration
  const platformConfig = useMemo(() => {
    return ColumnMappingService.getConfigForPlatform(upload.platform as Platform)
  }, [upload.platform])

  // Generate dynamic field configuration from platform config
  const dynamicFieldConfig = useMemo(() => {
    if (!platformConfig) {
      console.log('[FieldMapping] No platform config found, using default fields')
      return UNIFIED_FIELD_CONFIG
    }

    const config: Record<string, any> = {}

    // Add all identifier fields from platform config
    Object.keys(platformConfig.identifierFields).forEach(fieldKey => {
      const baseConfig = UNIFIED_FIELD_CONFIG[fieldKey as keyof typeof UNIFIED_FIELD_CONFIG]
      if (baseConfig) {
        config[fieldKey] = {
          ...baseConfig,
          required: platformConfig.requiredFields.identifierFields.includes(fieldKey)
        }
      } else {
        // Create default config for fields not in UNIFIED_FIELD_CONFIG
        const fieldLabels: Record<string, string> = {
          transactionId: 'Transaktions-ID',
          description: 'Beskrivning',
          date: 'Datum',
          tripCount: 'Antal resor',
          actualVAT: 'Aktuell moms',
          driverBonus: 'Förarbonus',
          cashReceived: 'Kontanter mottagna'
        }

        const fieldLabel = fieldLabels[fieldKey] || fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1)

        config[fieldKey] = {
          label: fieldLabel,
          icon: User,
          required: platformConfig.requiredFields.identifierFields.includes(fieldKey),
          type: 'identifier' as const
        }
      }
    })

    // Add all earnings fields from platform config
    Object.keys(platformConfig.earningsFields).forEach(fieldKey => {
      const baseConfig = UNIFIED_FIELD_CONFIG[fieldKey as keyof typeof UNIFIED_FIELD_CONFIG]
      if (baseConfig) {
        config[fieldKey] = {
          ...baseConfig,
          required: platformConfig.requiredFields.earningsFields.includes(fieldKey)
        }
      } else {
        // Create default config for fields not in UNIFIED_FIELD_CONFIG
        const fieldLabels: Record<string, string> = {
          transactionId: 'Transaktions-ID',
          description: 'Beskrivning',
          date: 'Datum',
          tripCount: 'Antal resor',
          actualVAT: 'Aktuell moms',
          driverBonus: 'Förarbonus',
          cashReceived: 'Kontanter mottagna'
        }

        const fieldLabel = fieldLabels[fieldKey] || fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1)

        config[fieldKey] = {
          label: fieldLabel,
          icon: DollarSign,
          required: platformConfig.requiredFields.earningsFields.includes(fieldKey),
          type: 'earnings' as const
        }
      }
    })

    console.log('[FieldMapping] Generated dynamic field config for platform:', upload.platform, config)
    return config
  }, [platformConfig, upload.platform])

  // Helper function to extract identifier data from a row
  const extractIdentifierData = (row: any, identifierMapping: Record<string, string>) => {
    const data: Record<string, any> = {}
    Object.entries(identifierMapping).forEach(([field, header]) => {
      if (header && row[header] !== undefined) {
        data[field] = row[header] || ''
      }
    })

    // Special case: If firstName and lastName are both mapped but driverName is not,
    // construct driverName from firstName + lastName (e.g., for Uber files)
    if (!identifierMapping.driverName && identifierMapping.firstName && identifierMapping.lastName) {
      const firstName = data.firstName || ''
      const lastName = data.lastName || ''
      data.driverName = `${firstName} ${lastName}`.trim()
    }

    return data
  }

  // Auto-detect fields when component mounts using the new column mapping service
  useEffect(() => {
    if (upload.headers.length > 0) {
      console.log('[FieldMapping] Running automatic column mapping suggestions')

      // Run platform detection for this file
      const platformInfo = PlatformDetectionService.detectPlatformFromFile(upload.headers)

      // Create column mapping suggestions
      const columnMappingResult = ColumnMappingService.createColumnMapping(
        upload.headers,
        platformInfo,
        upload.platform as Platform
      )

      console.log('[FieldMapping] Auto-detected column mapping:', {
        confidence: columnMappingResult.confidence,
        identifierFields: columnMappingResult.identifierFields,
        earningsFields: columnMappingResult.earningsFields
      })

      // Convert to legacy format for compatibility with existing code
      const legacyMapping = {
        identifierFields: columnMappingResult.identifierFields,
        earningsFields: columnMappingResult.earningsFields,
        confidence: columnMappingResult.confidence,
        detectedPlatform: platformInfo.detectedPlatform,
        manualOverride: columnMappingResult.manualOverride,
        unmappedHeaders: columnMappingResult.unmappedHeaders,
        missingRequiredFields: columnMappingResult.missingRequiredFields
      }

      setUnifiedMapping(legacyMapping)

      // If no manual mapping exists, use auto-detected suggestions
      if (!identifierMapping || Object.keys(identifierMapping).length === 0) {
        onIdentifierMappingChange(columnMappingResult.identifierFields)
      }
      if (!fieldMapping || Object.keys(fieldMapping).length === 0) {
        onFieldMappingChange(columnMappingResult.earningsFields)
      }
    }
  }, [upload.headers, upload.platform])

  // Update preview data when mapping changes
  useEffect(() => {
    if (upload.data.length > 0 && (
      (fieldMapping && Object.keys(fieldMapping).length > 0) ||
      (identifierMapping && Object.keys(identifierMapping).length > 0)
    )) {
      const sampleRows = upload.data.slice(0, 3)

      // Extract earnings data
      if (fieldMapping && Object.keys(fieldMapping).length > 0 && unifiedMapping) {
        const tempMapping = {
          ...unifiedMapping,
          identifierFields: identifierMapping || {},
          earningsFields: fieldMapping
        }
        const preview = sampleRows.map(row =>
          UnifiedColumnMappingService.extractEarningsData(row, tempMapping)
        )
        setPreviewData(preview)
      } else {
        setPreviewData([])
      }

      // Extract identifier data
      if (identifierMapping && Object.keys(identifierMapping).length > 0) {
        const identifierPreview = sampleRows.map(row =>
          extractIdentifierData(row, identifierMapping)
        )
        setPreviewIdentifierData(identifierPreview)
      } else {
        setPreviewIdentifierData([])
      }
    } else {
      setPreviewData([])
      setPreviewIdentifierData([])
    }
  }, [identifierMapping, fieldMapping, upload.data, unifiedMapping])

  const handleFieldChange = (fieldKey: string, columnName: string) => {
    const fieldConfig = UNIFIED_FIELD_CONFIG[fieldKey as keyof typeof UNIFIED_FIELD_CONFIG]

    // Check if user is trying to map driverName when it's already satisfied by firstName + lastName
    if (fieldKey === 'driverName' && columnName !== 'none') {
      const hasFirstName = identifierMapping && identifierMapping.firstName
      const hasLastName = identifierMapping && identifierMapping.lastName
      if (hasFirstName && hasLastName) {
        setShowDriverNameWarning(true)
      }
    } else if (fieldKey === 'driverName' && columnName === 'none') {
      // User is removing driverName mapping, hide warning
      setShowDriverNameWarning(false)
    }

    if (fieldConfig.type === 'identifier') {
      const newMapping = { ...(identifierMapping || {}) }
      if (columnName === 'none') {
        delete newMapping[fieldKey]
      } else {
        newMapping[fieldKey] = columnName
      }
      onIdentifierMappingChange(newMapping)
    } else {
      const newMapping = { ...(fieldMapping || {}) }
      if (columnName === 'none') {
        delete newMapping[fieldKey]
      } else {
        newMapping[fieldKey] = columnName
      }
      onFieldMappingChange(newMapping)
    }
  }

  const getCurrentValue = (fieldKey: string) => {
    const fieldConfig = dynamicFieldConfig[fieldKey]
    const mapping = fieldConfig?.type === 'identifier' ? identifierMapping : fieldMapping
    return (mapping && mapping[fieldKey]) || 'none'
  }

  const isDriverNameRequired = () => {
    // Driver name is not required if both firstName and lastName are mapped
    const hasFirstName = identifierMapping && identifierMapping.firstName
    const hasLastName = identifierMapping && identifierMapping.lastName
    return !(hasFirstName && hasLastName)
  }


  const getMappingStatus = () => {
    const requiredFields = Object.entries(dynamicFieldConfig)
      .filter(([fieldKey, config]) => {
        if (fieldKey === 'driverName') {
          return isDriverNameRequired()
        }
        return config.required
      })
      .map(([key, _]) => key)

    const mappedRequired = requiredFields.filter(field => {
      const fieldConfig = dynamicFieldConfig[field]
      const mapping = fieldConfig?.type === 'identifier' ? identifierMapping : fieldMapping
      return mapping && mapping[field]
    })

    const totalIdentifierMapped = identifierMapping ? Object.keys(identifierMapping).length : 0
    const totalEarningsMapped = fieldMapping ? Object.keys(fieldMapping).length : 0
    const totalMapped = totalIdentifierMapped + totalEarningsMapped

    const allRequiredMapped = mappedRequired.length === requiredFields.length

    if (allRequiredMapped) {
      return {
        status: 'complete' as const,
        message: `Alla obligatoriska fält mappade (${totalMapped} totalt)`,
        color: 'text-green-600',
        icon: CheckCircle2
      }
    } else {
      return {
        status: 'incomplete' as const,
        message: `${mappedRequired.length}/${requiredFields.length} obligatoriska fält mappade`,
        color: 'text-orange-600',
        icon: AlertTriangle
      }
    }
  }

  const status = getMappingStatus()
  const StatusIcon = status.icon

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card className={`border-2 ${status.status === 'complete' ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <StatusIcon className={`h-6 w-6 ${status.color}`} />
            <div>
              <p className={`font-medium ${status.color}`}>{status.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unified Field Mapping Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Columns className="h-5 w-5" />
            Kolumnidentifiering
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(dynamicFieldConfig).map(([fieldKey, config]) => {
              const Icon = config.icon
              const currentValue = getCurrentValue(fieldKey)
              const isMapped = currentValue !== 'none'
              const isFieldRequired = fieldKey === 'driverName'
                ? isDriverNameRequired()
                : config.required
              const isRequiredAndUnmapped = isFieldRequired && !isMapped

              // Check if driverName requirement is satisfied by firstName + lastName
              const isSatisfiedByAlternatives = fieldKey === 'driverName' &&
                                                !isMapped &&
                                                !isDriverNameRequired()

              return (
                <div
                  key={fieldKey}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isRequiredAndUnmapped
                      ? 'border-red-200 bg-red-50'
                      : isSatisfiedByAlternatives
                        ? 'border-gray-200 bg-gray-100 opacity-60'
                        : isMapped
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  {/* Field Icon and Label */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Icon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{config.label}</span>
                        {isFieldRequired && (
                          <span className="text-red-500 text-xs font-medium ml-1">*</span>
                        )}
                        {isSatisfiedByAlternatives && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Detta fält behöver inte mappas eftersom Förnamn och Efternamn redan är mappade.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dropdown */}
                  <div className="w-48">
                    <Select
                      value={currentValue}
                      onValueChange={(value) => handleFieldChange(fieldKey, value)}
                    >
                      <SelectTrigger className={`w-full h-8 text-sm ${
                        isRequiredAndUnmapped ? 'border-red-300' : ''
                      }`}>
                        <SelectValue placeholder="Välj kolumn" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ingen mappning</SelectItem>
                        {memoizedHeaders.map((header, index) => (
                          <SelectItem key={`${header}-${index}`} value={header}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Warning when user maps driverName despite firstName + lastName being mapped */}
                    {fieldKey === 'driverName' && isMapped && showDriverNameWarning && (
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Förarnamn kommer att användas istället för Förnamn + Efternamn
                      </p>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className="w-16 text-right">
                    {isMapped ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                    ) : isSatisfiedByAlternatives ? (
                      <div className="h-4 w-4" /> // No icon when satisfied by alternatives
                    ) : isFieldRequired ? (
                      <AlertTriangle className="h-4 w-4 text-red-600 ml-auto" />
                    ) : (
                      <div className="h-4 w-4" /> // placeholder for alignment
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Data Preview */}
      {(previewData.length > 0 || previewIdentifierData.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Förhandsvisning av data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground mb-4">
              Förhandsvisning av de första {Math.max(previewData.length, previewIdentifierData.length)} raderna med din mappning:
            </div>
            <ScrollArea className="h-64 border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rad</TableHead>
                    {/* Identifier columns */}
                    {identifierMapping && Object.keys(identifierMapping).length > 0 && (
                      <>
                        <TableHead>Förarnamn</TableHead>
                        <TableHead>Förar-ID</TableHead>
                        <TableHead>E-post</TableHead>
                      </>
                    )}
                    {/* Earnings columns */}
                    {fieldMapping && Object.keys(fieldMapping).length > 0 && (
                      <>
                        <TableHead>Bruttoinkomst</TableHead>
                        <TableHead>Nettoinkomst</TableHead>
                        <TableHead>Dricks</TableHead>
                      </>
                    )}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: Math.max(previewData.length, previewIdentifierData.length) }).map((_, index) => {
                    const earningsData = previewData[index]
                    const identifierData = previewIdentifierData[index]
                    const validation = earningsData ? UnifiedColumnMappingService.validateEarningsData(earningsData) : { isValid: true }

                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        {/* Identifier columns */}
                        {identifierMapping && Object.keys(identifierMapping).length > 0 && (
                          <>
                            <TableCell className="font-mono max-w-32 truncate">
                              {identifierData?.driverName || '-'}
                            </TableCell>
                            <TableCell className="font-mono max-w-24 truncate">
                              {identifierData?.driverId || '-'}
                            </TableCell>
                            <TableCell className="font-mono max-w-32 truncate">
                              {identifierData?.email || '-'}
                            </TableCell>
                          </>
                        )}
                        {/* Earnings columns */}
                        {fieldMapping && Object.keys(fieldMapping).length > 0 && (
                          <>
                            <TableCell className="font-mono">
                              {earningsData?.grossEarnings?.toFixed(2) || '-'}
                            </TableCell>
                            <TableCell className="font-mono">
                              {earningsData?.netEarnings?.toFixed(2) || '-'}
                            </TableCell>
                            <TableCell className="font-mono">
                              {earningsData?.tips?.toFixed(2) || '-'}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          {validation.isValid ? (
                            <Badge variant="outline" className="text-green-700 bg-green-50">
                              Giltig
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-700 bg-red-50">
                              Fel
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Validation Errors */}
      {previewData.some(data => !UnifiedColumnMappingService.validateEarningsData(data).isValid) && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Några rader innehåller ogiltiga värden. Kontrollera din fältmappning.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
